import { ActionTree } from "vuex";
import { PanelState } from "./panel.module";
import store, { RootState } from "..";
import { MUTATIONS } from "@/constants/mutations";
import { panelRequests } from "@/https/panel.https";
import { GlobalPagination } from "@/interfaces/panel/global-pagination.model";
import { ManualChargeApproveResult, ManualChargeFilters, ManualChargeListPayload, ManualChargeRejectResult } from "@/interfaces/panel/manual-charge.model";
import { UpdateGatewaySettlementSettingsPayload } from "@/interfaces/panel/gateway-settlement-settings";
import router from "@/router";
import { platformLogsRequests } from "@/https/platform-logs.https";

export const PANEL_ACTIONS: ActionTree<PanelState, RootState> = {
    // update active route page
    updateActiveRoutePage(context, route: string): void {
        context.commit(MUTATIONS.panel.updateActiveRoutePage, route);
    },
    updateLoadingPanel(context, loading: boolean): void {
        context.commit(MUTATIONS.panel.updateLoadingPanel, loading);
    },
    setGlobalPagination(context, pagination: GlobalPagination): void {
        context.commit(MUTATIONS.panel.setGlobalPagination, pagination);
    },
    setGlobalActivePage(context, page: number): void {
        context.commit(MUTATIONS.panel.setGlobalActivePage, page);
    },
    resetGlobalPagination(context): void {
        context.commit(MUTATIONS.panel.resetGlobalPagination);
    },

    // sayyarpay
    getTotalUsersRequest(): Promise<unknown> {
        return panelRequests.GetTotalUsers().then((response) => {
            return response.data.data as unknown;
        });
    },

    getDashboardStatsRequest(): Promise<unknown> {
        return panelRequests.GetDashboardStats().then((response) => {
            return response.data.data as unknown;
        });
    },

    getTotalTransactionsRequest(): Promise<unknown> {
        return panelRequests.GetTotalTransactions().then((response) => {
            return response.data.data as unknown;
        });
    },

    getTotalCommisionsRequest(): Promise<unknown> {
        return panelRequests.GetTotalCommisions().then((response) => {
            return response.data.data as unknown;
        });
    },

    getTotalWalletsRequest(): Promise<unknown> {
        return panelRequests.GetTotalWallets().then((response) => {
            return response.data.data as unknown;
        });
    },

    getAllUsersRequest(context, data: { type?: string, filters?: { email?: string; phone?: string; name?: string }; page?: number }): void {
        panelRequests.GetAllUsers(data.type, data.filters, data.page).then((response) => {
            context.state.allUsers = response.data.data.users;
        }).catch((error) => {
            console.log("get users list error ===>", error);
        })
    },

    getNotVerifiedUsersRequest(context, page = 1): void {
        panelRequests.GetNotVerifiedUsers(page, 50).then((response) => {
            context.state.notVerifiedUsers = response.data.data.users;
        }).catch((error) => {
            console.log("get not verified users error ===>", error);
        })
    },

    getSystemLogsRequest(context): void {
        panelRequests.GetSystemLogs().then((response) => {
            context.state.systemLogs = response.data.data.logs;
        }).catch((error) => {
            console.log("get system logs ===>", error);
        })
    },

    getUserFinancialLogsRequest(context, userId: string): void {
        panelRequests.GetUserFinancialLogs(userId).then((response) => {
            context.state.userFinancialLogs = response.data.data.logs;
        }).catch((error) => {
            console.log("get system logs ===>", error);
        })
    },

    getUserTransactionsRequest(context, userId: string): void {
        panelRequests.GetUserTransactions(userId).then((response) => {
            context.state.userTransactions = response.data.data.transactions;
        })
    },

    getExternalTransactionsRequest(context, params: any): void {
        panelRequests.GetExternalTransactions(params).then((response) => {
            context.state.externalTransactions = response.data.data.transactions;
            context.state.totalTransactionsAmount = response.data.data.totalAmount;
        }).catch((error) => {
            console.log("get external transactions error ===>", error);
        })
    },

    getUserExternalTransactionsRequest(context, userId: string): void {
        panelRequests.GetUserExternalTransactions(userId).then((response) => {
            context.state.userExternalTransactions = response.data.data.transactions;
        })
    },

    getAllWithdrawalsRequest(context): void {
        panelRequests.getAllWithdrawals().then((response) => {
            context.state.allWithdrawals = response.data.data.transactions;
        }).catch((error) => {
            console.log("get withdrawals error ===>", error);
        })
    },

    getUserWithdrawalsRequest(context, userId: string): void {
        panelRequests.GetUserWithdrawals(userId).then((response) => {
            context.state.userWithdrawals = response.data.data.transactions;
        }).catch((error) => {
            console.log("get withdrawals error ===>", error);
        })
    },

    chargeWalletAccountRequest(context, chargeData: { userId: string, type: string, amount: string, description: string }): void {
        const data = {
            userId: chargeData.userId,
            amount: Number(chargeData.amount)
        };
        panelRequests.ChargeUserWallet(data).then(() => {
            context.dispatch("getAllUsersRequest");
        })
    },

    dechargeWalletAccountRequest(context, chargeData: { userId: string, type: string, amount: string, description: string }): void {
        const data = {
            userId: chargeData.userId,
            amount: Number(chargeData.amount)
        };
        panelRequests.DechargeUserWallet(data).then(() => {
            context.dispatch("getAllUsersRequest");
        })
    },

    getUserInfoRequest(context, userId: string): void {
        panelRequests.GetUserInfo(userId).then((response) => {
            const user = response?.data.data;
            if (user) {
                context.state.userInfo = user;
            } else {
                context.state.userInfo = {
                    id: "",
                    name: "",
                    email: "",
                    image: "",
                    wallet: "",
                    withdrawn: "",
                    role: ""
                }
            }
        })
    },

    getUserAcceptedAuthRequest(context, userId: string): void {
        panelRequests.GetUserAcceptedAuth(userId).then((response) => {
            const document = response?.data.data;
            if (document) {
                context.state.userAcceptedAuth = document;
            } else {
                context.state.userAcceptedAuth = {
                    id: "",
                    userId: "",
                    trackingCode: "",
                    firstName: "",
                    nationalCode: "",
                    birthDate: "",
                    gender: "",
                    cardBackNumber: "",
                    nationalImageUrl: "",
                    selfieImageUrl: "",
                    status: "",
                    submittedAt: "",
                    reviewedAt: "",
                    reviewNotes: ""
                }
            }
        })
    },

    sendAccepctUserIdentifyRequest(context, userId: string): void {
        panelRequests.AcceptUserIdentify(userId).then((response) => {
            console.log("accept user identify ===>", response);
            context.dispatch("getNotVerifiedUsersRequest");
        }).catch((error) => {
            context.dispatch("getNotVerifiedUsersRequest");
            console.log("accept user identify error ===>", error);
        })
    },

    sendRejectUserIdentifyRequest(context, rejectData: { userId: string, message: string }): void {
        panelRequests.RejectUserIdentify(rejectData).then((response) => {
            console.log("accept user identify ===>", response);
            context.dispatch("getNotVerifiedUsersRequest");
        }).catch((error) => {
            context.dispatch("getNotVerifiedUsersRequest");
            console.log("accept user identify error ===>", error);
        })
    },

    getSystemSettingRequest(context): void {
        panelRequests.GetSystemSettings().then((response) => {
            context.state.systemSettings = response.data.data;
        }).catch((error) => {
            console.log("get system setting error ===>", error);
        })
    },

    sendSystemEditRequest(context, systemSettingData: Record<string, unknown> & { id: string }): void {
        const updatesArray: Array<{ field: string; value: unknown }> = [];

        for (const [field, value] of Object.entries(systemSettingData)) {
            if (field === 'id' || field === 'updatedAt' || field === 'cardNumber') {
                continue;
            }

            let processedValue: unknown = value;
            if (typeof value === 'string') {
                processedValue = Number(value);
            }

            updatesArray.push({
                field,
                value: processedValue
            });
        }

        panelRequests.SendSystemSettingEdit(systemSettingData.id, updatesArray).then((response) => {
            console.log("update system settings ===>", response);
        }).catch((error) => {
            console.log("update system settings error ===>", error);
        });
    },

    getGatewaysRequest(context, type?: string): void {
        panelRequests.GetGateways(type).then((response) => {
            context.state.gateways = response.data.data.gateways;
        }).catch((error) => {
            console.log("get gateways error ===>", error);
        })
    },

    getGatewayInfoRequest(context, gatewayId: string): void {
        panelRequests.GetGatewayInfo(gatewayId).then((response) => {
            console.log("get gateway info response ===>", response.data.data);
            context.state.gatewayInfo = response.data.data;
        }).catch((error) => {
            console.log("get gateway info error ===>", error);
        })
    },

    getGatewayTransactionsRequest(context, gatewayId: string): void {
        panelRequests.GetGateWayTransactions(gatewayId).then((response) => {
            context.state.gatewayTransactions = response.data.data.transactions;
        }).catch((error) => {
            console.log("get gateway transactions error ===>", error);
        })
    },

    toggleGatewayStatusRequest(context, toggleGatewayData: { gatewayId: string, status: string, rejectMessage?: string }): void {
        panelRequests.ToggleGatewayStatus(toggleGatewayData).then(() => {
            context.dispatch("getGatewaysRequest", 'APPROVED');
        }).catch((error) => {
            console.log("toggle gateway status error ===>", error);
        })
    },

    getCommissionsRequest(context): void {
        panelRequests.GetCommissions().then((response) => {
            context.state.commissions = response.data.data.commissions;
        }).catch((error) => {
            console.log("get commissions list error ===>", error);
        })
    },

    getDepositTransferHistoryRequest(context): void {
        panelRequests.GetDepositTransferHistory().then((response) => {
            context.state.depositTransferHistory = response.data.data.statements;
        }).catch((error) => {
            console.log("get deposit transfer history error ===>", error);
        })
    },

    async getManualChargesRequest(context, payload?: Partial<ManualChargeFilters>): Promise<void> {
        const previousFilters = context.state.manualChargesFilters;
        const statusCandidate = payload?.status ?? previousFilters.status;
        const allowedStatuses: ManualChargeFilters["status"][] = ["PENDING", "SUCCESS", "REJECTED", "ALL"];
        const normalizedStatus = typeof statusCandidate === "string"
            ? (statusCandidate.toUpperCase() as ManualChargeFilters["status"])
            : previousFilters.status;
        const finalStatus = allowedStatuses.includes(normalizedStatus) ? normalizedStatus : "ALL";

        const chargeTypeCandidate = payload?.chargeType ?? previousFilters.chargeType;
        const allowedChargeTypes: Array<"RIALI" | "CRYPTO" | "ALL"> = ["RIALI", "CRYPTO", "ALL"];
        const normalizedChargeType = typeof chargeTypeCandidate === "string"
            ? (chargeTypeCandidate.toUpperCase() as "RIALI" | "CRYPTO" | "ALL")
            : (previousFilters.chargeType ?? "ALL");
        const finalChargeType = allowedChargeTypes.includes(normalizedChargeType) ? normalizedChargeType : "ALL";

        const nextFilters: ManualChargeFilters = {
            status: finalStatus,
            chargeType: finalChargeType,
            page: Number(payload?.page ?? previousFilters.page) || 1,
            pageSize: Number(payload?.pageSize ?? previousFilters.pageSize) || 20,
        };

        nextFilters.page = Math.max(1, Math.floor(nextFilters.page));
        nextFilters.pageSize = Math.min(100, Math.max(1, Math.floor(nextFilters.pageSize)));

        context.state.manualChargesFilters = nextFilters;
        context.state.manualChargesLoading = true;

        const requestParams = {
            status: nextFilters.status === "ALL" ? undefined : nextFilters.status,
            chargeType: nextFilters.chargeType === "ALL" ? undefined : nextFilters.chargeType,
            page: nextFilters.page,
            pageSize: nextFilters.pageSize,
        };

        try {
            const response = await panelRequests.GetManualCharges(requestParams);
            const payloadData = response?.data?.data as ManualChargeListPayload | undefined;

            if (payloadData) {
                context.state.manualCharges = payloadData.items ?? [];
                const pagination = payloadData.pagination ?? {
                    page: nextFilters.page,
                    pageSize: nextFilters.pageSize,
                    totalItems: payloadData.items?.length ?? 0,
                    totalPages: payloadData.items && payloadData.items.length > 0 ? 1 : 0,
                };

                context.state.manualChargesPagination = pagination;
                context.state.manualChargesFilters = {
                    ...nextFilters,
                    page: pagination.page ?? nextFilters.page,
                    pageSize: pagination.pageSize ?? nextFilters.pageSize,
                };
            } else {
                context.state.manualCharges = [];
                context.state.manualChargesPagination = {
                    page: nextFilters.page,
                    pageSize: nextFilters.pageSize,
                    totalItems: 0,
                    totalPages: 0,
                };
            }
        } catch (error) {
            console.log("get manual charges error ===>", error);
            const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
            const message = axiosError.response?.data?.error
                ?? axiosError.message
                ?? "خطا در دریافت لیست شارژ دستی";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            context.state.manualCharges = [];
            context.state.manualChargesPagination = {
                page: nextFilters.page,
                pageSize: nextFilters.pageSize,
                totalItems: 0,
                totalPages: 0,
            };
        } finally {
            context.state.manualChargesLoading = false;
        }
    },

    async approveManualChargeRequest(context, payload: { manualChargeId: string; amount?: number }): Promise<void> {
        const { manualChargeId, amount } = payload;
        if (!manualChargeId) {
            return;
        }

        context.state.manualChargeActionLoading = {
            ...context.state.manualChargeActionLoading,
            [manualChargeId]: true,
        };

        try {
            const response = await panelRequests.ApproveManualCharge(manualChargeId, amount);
            const payloadData = response?.data?.data as ManualChargeApproveResult | undefined;
            const message = response?.data?.message ?? "شارژ دستی تایید شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "success",
                message,
            }, { root: true });

            if (payloadData?.manualCharge) {
                context.state.manualCharges = context.state.manualCharges.map((charge) =>
                    charge.id === payloadData.manualCharge.id ? payloadData.manualCharge : charge
                );
            }

            await context.dispatch("getManualChargesRequest");
        } catch (error) {
            console.log("approve manual charge error ===>", error);
            const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
            const message = axiosError.response?.data?.error
                ?? axiosError.message
                ?? "تایید شارژ دستی با خطا مواجه شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            throw error;
        } finally {
            const loadingState = { ...context.state.manualChargeActionLoading };
            delete loadingState[manualChargeId];
            context.state.manualChargeActionLoading = loadingState;
        }
    },

    async rejectManualChargeRequest(context, manualChargeId: string): Promise<void> {
        if (!manualChargeId) {
            return;
        }

        context.state.manualChargeActionLoading = {
            ...context.state.manualChargeActionLoading,
            [manualChargeId]: true,
        };

        try {
            const response = await panelRequests.RejectManualCharge(manualChargeId);
            const message = response?.data?.message ?? "شارژ دستی رد شد";
            const payloadData = response?.data?.data as ManualChargeRejectResult | undefined;

            if (payloadData?.manualCharge) {
                context.state.manualCharges = context.state.manualCharges.map((charge) =>
                    charge.id === payloadData.manualCharge.id ? payloadData.manualCharge : charge
                );
            }

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "success",
                message,
            }, { root: true });

            await context.dispatch("getManualChargesRequest");
        } catch (error) {
            console.log("reject manual charge error ===>", error);
            const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
            const message = axiosError.response?.data?.error
                ?? axiosError.message
                ?? "رد شارژ دستی با خطا مواجه شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            throw error;
        } finally {
            const loadingState = { ...context.state.manualChargeActionLoading };
            delete loadingState[manualChargeId];
            context.state.manualChargeActionLoading = loadingState;
        }
    },

    async createCryptoManualChargeRequest(context, payload: Record<string, unknown>): Promise<void> {
        try {
            const response = await panelRequests.CreateCryptoManualCharge(payload);
            const message = response?.data?.message ?? "درخواست شارژ دستی کریپتو با موفقیت ثبت شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "success",
                message,
            }, { root: true });

            await context.dispatch("getManualChargesRequest");
        } catch (error) {
            console.log("create crypto manual charge error ===>", error);
            const axiosError = error as { response?: { data?: { error?: string; details?: unknown } }; message?: string };
            const message = axiosError.response?.data?.error
                ?? axiosError.message
                ?? "ایجاد شارژ دستی کریپتو با خطا مواجه شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            throw error;
        }
    },

    async getGatewaySettlementSettingsRequest(context, gatewayId: string): Promise<void> {
        context.state.gatewaySettlementSettings = null;
        try {
            const response = await panelRequests.GetGatewaySettlementSettings(gatewayId);
            context.state.gatewaySettlementSettings = response.data.data;
        } catch (error) {
            console.error("get gateway settlement settings error ===>", error);
            const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
            const message = axiosError.response?.data?.message
                ?? axiosError.message
                ?? "دریافت تنظیمات تسویه درگاه با خطا مواجه شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            router.push({ name: "Gateways" });
        }
    },

    async updateGatewaySettlementSettings(context, payload: UpdateGatewaySettlementSettingsPayload): Promise<void> {
        try {
            const response = await panelRequests.UpdateGatewaySettlementSettings(payload.gatewayId, payload.data);
            context.state.gatewaySettlementSettings = response.data.data;
            context.dispatch("app/updateToastModal", {
                status: true,
                type: "success",
                message: response.data.message ?? "تنظیمات تسویه درگاه ذخیره شد",
            }, { root: true });
        } catch (error) {
            console.error("update gateway settlement settings error ===>", error);
            const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
            const message = axiosError.response?.data?.message
                ?? axiosError.message
                ?? "ذخیره تنظیمات تسویه درگاه با خطا مواجه شد";

            context.dispatch("app/updateToastModal", {
                status: true,
                type: "danger",
                message,
            }, { root: true });

            throw error;
        }
    },

    getWithdrawalsCryptoRequest(context): Promise<void> {
        return panelRequests.GetWithdrawalsCrypto().then((response) => {
            context.state.withdrawalsCrypto = response.data.data.transactions;
        }).catch((error) => {
            console.log("get withdrawals crypto error ===>", error);
        });
    },

    acceptWithdrawalUsdtCharge(context, chargeData): void {
        panelRequests.AcceptUsdtCharge(chargeData).then(() => {
            store.dispatch("panel/getWithdrawalsCryptoRequest");
        })
    },

    rejectWithdrawalUsdtCharge(context, reChargeData): void {
        panelRequests.RejectUsdtCharge(reChargeData).then(() => {
            store.dispatch("panel/getWithdrawalsCryptoRequest");
        })
    },

    getAcceptedWithdrawalsCryptoRequest(context): Promise<void> {
        return panelRequests.GetAcceptedWithdrawalsCrypto().then((response) => {
            console.log("accepted response ===>", response);
            context.state.withdrawalsCrypto = response.data.data.transactions;
        }).catch((error) => {
            console.log("get withdrawals crypto error ===>", error);
        });
    },

    getRejectedWithdrawalsCryptoRequest(context): Promise<void> {
        return panelRequests.GetRejectedWithdrawalsCrypto().then((response) => {
            console.log("rejected response ===>", response);
            context.state.withdrawalsCrypto = response.data.data.transactions;
        }).catch((error) => {
            console.log("get withdrawals crypto error ===>", error);
        });
    },

    async getManualChargeStats(): Promise<unknown> {
        try {
            const response = await panelRequests.GetManualChargeStats();
            return response.data.data as unknown;
        } catch (error) {
            console.log("get manual charge stats error ===>", error);
            throw error;
        }
    },

    async getUserStatementRequest(context, payload: {
        userId: string;
        page?: number;
        pageSize?: number;
        from?: string;
        to?: string;
        category?: "charge" | "payment" | "withdraw" | "transfer" | "commission" | "settlement" | "crypto" | "all";
        currency?: string;
        minAmount?: number;
        maxAmount?: number;
        status?: "SUCCESS" | "FAILED" | "PENDING" | "CANCELLED";
        action?: string;
    }): Promise<void> {
        try {
            context.state.loading = true;

            // Build filters object with only defined values
            const filters: {
                page?: number;
                pageSize?: number;
                from?: string;
                to?: string;
                category?: "charge" | "withdraw" | "transfer" | "crypto_withdraw" | "riali_withdraw" | "trade";
                currency?: string;
                minAmount?: number;
                maxAmount?: number;
                status?: "SUCCESS" | "FAILED" | "PENDING";
                action?: string;
            } = {};

            if (payload.page) filters.page = payload.page;
            if (payload.pageSize) filters.pageSize = payload.pageSize;
            if (payload.from) filters.from = payload.from;
            if (payload.to) filters.to = payload.to;
            if (payload.category) filters.category = payload.category;
            if (payload.currency) filters.currency = payload.currency;
            if (payload.minAmount !== undefined) filters.minAmount = payload.minAmount;
            if (payload.maxAmount !== undefined) filters.maxAmount = payload.maxAmount;
            if (payload.status) filters.status = payload.status;
            if (payload.action) filters.action = payload.action;

            const response = await platformLogsRequests.GetUserStatement(payload.userId, filters);

            if (response.data && response.data.success) {
                context.state.userStatement = {
                    logs: response.data.data.logs || [],
                    summary: response.data.data.summary || {
                        totalRiali: 0,
                        totalUSDT: 0,
                        totalLogs: 0,
                        financialBreakdown: undefined
                    },
                    pagination: response.data.data.pagination || {
                        page: 1,
                        pageSize: 50,
                        totalItems: 0,
                        totalPages: 0,
                        hasMore: false
                    },
                    user: response.data.data.user || null
                };
            }
        } catch (error) {
            console.log("get user statement error ===>", error);
            context.state.userStatement = {
                logs: [],
                summary: {
                    totalRiali: 0,
                    totalUSDT: 0,
                    totalLogs: 0,
                    financialBreakdown: undefined
                },
                pagination: {
                    page: 1,
                    pageSize: 50,
                    totalItems: 0,
                    totalPages: 0,
                    hasMore: false
                },
                user: null
            };
        } finally {
            context.state.loading = false;
        }
    },

    sendNotificationRequest(context, notificationData: { type: string, message: string }): Promise<void> {
        return panelRequests.SendNotificationRequest(notificationData).then((response) => {
            console.log("rejected response ===>", response);
        }).catch((error) => {
            console.error("send group notification error ===>", error);
            throw error;
        });
    },

    sendUserNotificationRequest(context, notificationData: { type: string, message: string, desc: string, user: string, metadata: any }): Promise<void> {
        console.log("notification in action ===>", notificationData);
        return panelRequests.SendUserNotificationRequest(notificationData).then((response) => {
            console.log("rejected response ===>", response);
        }).catch((error) => {
            console.error("send user notification error ===>", error);
            throw error;
        });
    },

    getPaymentSettingRequest(context): void {
        panelRequests.GetPaymentSettings().then((response) => {
            context.state.paymentSetting = response.data.data;
            console.log("get payment setting ===>", response.data.data);
        }).catch((error) => {
            console.log("get system setting error ===>", error);
        })
    },

    updatePaymentSettingRequest(context, form): void {
        panelRequests.UpdatePaymentSettings(form).then((response) => {
            context.state.paymentSetting = response.data.data;
            store.dispatch("panel/getPaymentSettingRequest");
        }).catch((error) => {
            console.log("get system setting error ===>", error);
        })
    },

    getExchangeSettingRequest(context): void {
        panelRequests.GetExchangeSettings().then((response) => {
            context.state.exchangeSetting = response.data.data;
        }).catch((error) => {
            console.log("get exchange setting ===>", error);
        })
    },

    getTransactionReportExcelRequest(context, params: any) {
        panelRequests.getTransactionReportExcel(params).then((response) => {
            const { base64, fileName } = response.data;

            // Create download link
            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        })
    },

    editExchangeSettingRequest(context, settingData: { settingId: string, setting: any }): void {
        panelRequests.EditExchangeSetting(settingData.settingId, settingData.setting).then((response) => {
            console.log("edit exchange setting ===>", response);
            store.dispatch("panel/getExchangeSettingRequest");
        }).catch((error) => {
            console.log("edit exchange setting error ===>", error);
        })
    }
};
